import { chartLayoutColours } from '@risksmart-app/components/src/utils/colours';
import type { Chart, ChartOptions } from 'highcharts';
import Highcharts from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import { forwardRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDashboardWidgetSettings } from 'src/context/useDashboardWidgetSettings';
import { useDashboardBulkExportStore } from 'src/pages/dashboards/useDashboardBulkExportStore';
import { useWidgetContext } from 'src/pages/dashboards/widget-context/WidgetContext';

import { useGetChartCanvas } from '../../components/highcharts/hooks/useGetChartCanvas';
import { useMergeChartOptions } from '../../components/highcharts/hooks/useMergeChartOptions';
import { RSHighcharts } from '../../components/highcharts/RSHighcharts';
import { toExportFilename } from './exportFilename';

type RSHighchartsProps = {
  options: Highcharts.Options;
};

type HighchartsOptionsWithRequiredLoad = Omit<Highcharts.Options, 'chart'> & {
  chart: Omit<ChartOptions, 'events'> & {
    events: {
      load: (this: Chart, event: Event) => void;
    };
  };
};

// Todo: Figure out how to get working with styledMode and style modules
// Font family must be set globally for Highcharts to apply correctly
Highcharts.setOptions({
  chart: {
    style: {
      fontFamily: 'Sora, sans-serif',
    },
  },
});

export const HighchartsWidget = forwardRef<
  HighchartsReactRefObject,
  RSHighchartsProps
>(({ options }, ref) => {
  const widgetData = useWidgetContext();
  const { t } = useTranslation(['common'], { keyPrefix: 'export' });
  const [widgetSettings] = useDashboardWidgetSettings<{
    title?: string;
  }>();

  // The title shown on the widget header: a user-set title (universal widgets)
  // falls back to the widget definition's translated title (pre-built widgets,
  // provided via WidgetContext). Used for the export chart title and the
  // download / zip filename so both match what's on screen.
  const displayTitle = widgetSettings?.title ?? widgetData?.title;

  // Mirror the on-screen board header in the export title: alignment follows
  // the widget's centerAlignHeader, and font size/weight follow the header
  // variant (h3 = large 20px/700, h4 = small 14px/600) which itself depends on
  // the widget's current size. `value` tiles stay centre-aligned.
  const isLargeHeader = widgetData?.headerVariant === 'h3';
  // On screen the board header is centred for the small (h4) variant and
  // left-aligned for the large (h3) variant (see BoardItem). Mirror that, and
  // keep value tiles centred.
  const exportTitleAlign: 'center' | 'left' =
    options.chart?.type === 'value' || !isLargeHeader ? 'center' : 'left';
  const exportTitleStyle = {
    fontSize: isLargeHeader ? '20px' : '14px',
    fontWeight: isLargeHeader ? '700' : '600',
    color: chartLayoutColours.titles,
  };

  const {
    addWidgetExport: addBulkWidgetExport,
    removeWidgetExport: removeBulkWidgetExport,
  } = useDashboardBulkExportStore();
  const getChartCanvas = useGetChartCanvas();

  useEffect(() => {
    return () => {
      removeBulkWidgetExport(widgetData?.widgetId || '');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaults: HighchartsOptionsWithRequiredLoad = {
    accessibility: {
      enabled: false,
    },
    exporting: {
      enabled: true,
      chartOptions: {
        title: {
          text: displayTitle || '',
          align: exportTitleAlign,
          style: exportTitleStyle,
        },
        // Pie/donut charts use the `title` slot for the centre label, so the
        // widget title has nowhere to go in the export. Surface it via the
        // subtitle (top, title-styled, matching the on-screen header) for those
        // types. Every other chart keeps its on-screen subtitle text, just
        // rendered visually secondary to the title.
        subtitle:
          options.chart?.type === 'pie'
            ? {
                text: displayTitle || '',
                align: exportTitleAlign,
                verticalAlign: 'top',
                style: exportTitleStyle,
              }
            : {
                style: {
                  fontSize: '12px',
                  fontWeight: '600',
                },
              },
        // Wrap long category-axis labels in exports instead of truncating
        // them with an ellipsis, matching the on-screen axis rendering.
        xAxis: {
          labels: {
            style: {
              textOverflow: 'none',
              whiteSpace: 'normal',
            },
          },
        },
        plotOptions: {
          series: {
            dataLabels: {
              // Don't force value tags onto every exported chart — only the
              // single-value widget needs them. Other chart types inherit
              // their on-screen data-label setting so the export matches.
              enabled: options.chart?.type === 'value',
            },
          },
        },
      },
      buttons: undefined,
      fallbackToExportServer: false,
    },
    title: {
      text: undefined,
    },
    // Subtitle is used as the main browser display title, with title set only for exports (generally
    // the board item header)
    subtitle: {
      align: 'left',
      style: {
        fontSize: '14px',
        fontWeight: '700',
        color: chartLayoutColours.titles,
      },
    },
    yAxis: {
      title: { text: '' },
    },
    chart: {
      events: {
        load: function (this: Chart) {
          // Highcharts makes a minimal copy of the chart (without this.exporting.exportChart)
          // when exporting which fires the load function again - we can check for this with
          // the forExport property and not reset the export functions
          if (
            !widgetData ||
            this.renderer?.forExport ||
            this.options?.chart?.type === 'richText'
          ) {
            return;
          }

          // Register export function for bulk export (triggered from dashboard level)
          addBulkWidgetExport({
            id: widgetData.widgetId,
            filename: toExportFilename(displayTitle),
            exportFn: async () => {
              const canvas = await getChartCanvas(this);

              return canvas ? { kind: 'image', canvas } : undefined;
            },
          });

          // Set up custom export buttons on the chart (triggered from individual widget level)
          // Pass the chart's live on-screen size as the export source size so
          // the export scales with the widget instead of falling back to
          // Highcharts' default 600x400, which clips axis labels and stacked
          // data-label tags on taller widgets (RSP-4105).
          const exportOptions = () => ({
            // Name the downloaded file after the widget title. Highcharts
            // appends the extension.
            filename: toExportFilename(displayTitle),
            // Respect a chart-specific export size when one is set (e.g. the
            // pie/donut enlarges its canvas + adds spacing so external data
            // labels aren't clipped). Only fall back to the live widget size
            // for charts that don't manage their own export dimensions —
            // otherwise we'd override the bespoke size but keep its spacing,
            // collapsing the plot.
            sourceWidth: this.options.exporting?.sourceWidth ?? this.chartWidth,
            sourceHeight:
              this.options.exporting?.sourceHeight ?? this.chartHeight,
          });

          widgetData.setExportFns?.([
            {
              id: 'exportPNG',
              text: t('images.export_png'),
              fn: () => {
                this.exporting.exportChart({
                  type: 'image/png',
                  ...exportOptions(),
                });
              },
            },
            {
              id: 'exportJPEG',
              text: t('images.export_jpeg'),
              fn: () => {
                this.exporting.exportChart({
                  type: 'image/jpeg',
                  ...exportOptions(),
                });
              },
            },
            {
              id: 'exportSVG',
              text: t('images.export_svg'),
              fn: () => {
                this.exporting.exportChart({
                  type: 'image/svg+xml',
                  ...exportOptions(),
                });
              },
            },
          ]);
        },
      },
    },
  };

  const mergedOptions = useMergeChartOptions(defaults, options);

  return (
    <RSHighcharts
      ref={ref}
      options={mergedOptions}
      // Explicitly set container height to ensure it fills the parent
      // otherwise chart gets default height of 400px
      containerProps={{
        style: { height: '99%' },
      }}
    />
  );
});
